import { ProfileTitle } from '@components/Profile/ProfileTitle/ProfileTitle'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Vocabulary } from '@interfaces/index'
import DotSpinner from '@components/DotSpinner/DotSpinner'
import { VelvetSDK } from 'sdk'
import './AppLanguage.scss'
import useGoBackNavigation from '@hooks/useGoBackNavigation'
import classNames from 'classnames'
import { SecondaryButton } from '@components/Buttons/SecondaryButton/SecondaryButton'
import { onEnterKeyPressAndClick } from '@utils/helpers'
import { Params, useNavigate, useParams } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { RightArrow } from '@assets/index'
import { KeyName, LocalStorageItem } from '@constants/index'
import LanguageButton from '@components/Buttons/LanguageButton/LanguageButton'
import { useUserProfile } from '@hooks/useUserProfile'
import { setProfileLang } from '@actions/profileActions'
import { useTypedSelector } from 'store'

interface AppLanguageProps {
  showMenu: (status: boolean) => void
}

const AppLanguage = ({ showMenu }: AppLanguageProps) => {
  const config = VelvetSDK.Configuration.configuration
  const [isLoading, setIsLoading] = useState(false)
  const V = VelvetSDK.Vocabulary.vocabulary
  const userObject = JSON.parse(localStorage.getItem(LocalStorageItem.USER_DATA) as string)
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const cancelButtonRef = useRef<HTMLButtonElement>(null)
  const selectedLanguageRef = useRef<HTMLDivElement>(null)
  const firstColRef = useRef<HTMLDivElement>(null)
  const lastColRef = useRef<HTMLDivElement>(null)
  const { profileId } = useParams<Params>()
  const { profileData } = useUserProfile({
    profileId: profileId || '',
  })
  const navigationDirectionRef = useRef<'next' | 'prev' | null>(null)
  const [chooseLang, setChooseLang] = useState(profileData?.preferredLanguage)
  const [vocabList, setVocabList] = useState<Vocabulary[]>([])
  const [currentStartColumn, setCurrentStartColumn] = useState(0)
  const [isTransitioningRight, setIsTransitioningRight] = useState(false)
  const [isTransitioningLeft, setIsTransitioningLeft] = useState(false)
  const COLUMNS_PER_PAGE = 3
  const COLUMN_SIZE = 7
  const totalColumns = Math.ceil(vocabList && vocabList.length / COLUMN_SIZE)
  const hasNextPage = currentStartColumn + COLUMNS_PER_PAGE < totalColumns
  const hasPrevPage = currentStartColumn > 0
  const apiEndpointBaseUrl = config?.apiEndpointBaseUrl
  const profileLangLocal = useTypedSelector((state) => state.profile.lang)

  const sortByVocabKey = useCallback(
    (vocabArray: Vocabulary[]) => {
      setIsLoading(true)
      const preferredLang = profileData?.preferredLanguage
      const sorted = [...vocabArray].sort((a, b) => a.vocabKey.localeCompare(b.vocabKey))
      // Move the preferred language to the front
      const preferred = sorted.find((item) => item.language === preferredLang)
      const others = sorted.filter((item) => item.language !== preferredLang)
      setIsLoading(false)
      return preferred ? [preferred, ...others] : sorted
    },
    [profileData?.preferredLanguage],
  )

  useGoBackNavigation()

  const handleSave = useCallback(
    (e: React.KeyboardEvent | React.MouseEvent) => {
      e.preventDefault()

      onEnterKeyPressAndClick(e, async () => {
        if (chooseLang) navigate(-1)
      })
    },
    [chooseLang, navigate],
  )

  const handleCancel = useCallback(() => {
    navigate(-1)
  }, [navigate])

  const handleNextPage = useCallback(() => {
    if (hasNextPage) {
      setIsTransitioningRight(true)
      navigationDirectionRef.current = 'next'
      setCurrentStartColumn(currentStartColumn + COLUMNS_PER_PAGE)
      //removes animation class after transition completes (matches with CSS transition duration)
      setTimeout(() => {
        setIsTransitioningRight(false)
      }, 200)
    }
  }, [currentStartColumn, hasNextPage])

  const handlePrevPage = useCallback(() => {
    if (hasPrevPage) {
      setIsTransitioningLeft(true)
      navigationDirectionRef.current = 'prev'
      setCurrentStartColumn(Math.max(0, currentStartColumn - COLUMNS_PER_PAGE))
      //removes animation class after transition completes (matches with CSS transition duration)
      setTimeout(() => {
        setIsTransitioningLeft(false)
      }, 200)
    }
  }, [currentStartColumn, hasPrevPage])

  const getLanguageColumns = useCallback(() => {
    const columns = []
    // Create columns from the current starting column
    for (let col = currentStartColumn; col < currentStartColumn + COLUMNS_PER_PAGE + 1; col++) {
      if (col < totalColumns) {
        const columnItems = []
        const startIdx = col * COLUMN_SIZE
        const endIdx = Math.min(startIdx + COLUMN_SIZE, vocabList.length)

        for (let i = startIdx; i < endIdx; i++) {
          columnItems.push(vocabList[i])
        }

        columns.push(columnItems)
      }
    }
    return columns
  }, [currentStartColumn, totalColumns, vocabList])

  const languageColumns = getLanguageColumns()
  const visibleColumns = languageColumns.slice(0, 4)

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent | React.MouseEvent, colIndex: number, language: string) => {
      // when in the third column and pressing right, go to next page
      if ((e as React.KeyboardEvent).key === KeyName.ArrowRight) {
        if (colIndex === 2 && hasNextPage) {
          handleNextPage()
          e.preventDefault()
        }
      }

      // when in the first column and pressing left, go to prev page
      if ((e as React.KeyboardEvent).key === KeyName.ArrowLeft) {
        if (colIndex === 0 && hasPrevPage) {
          handlePrevPage()
          e.preventDefault()
        }
      }

      onEnterKeyPressAndClick(e, async () => {
        if (apiEndpointBaseUrl && userObject) {
          setChooseLang(language)
          dispatch(setProfileLang(language))
        }
      })
    },
    [apiEndpointBaseUrl, dispatch, handleNextPage, handlePrevPage, hasNextPage, hasPrevPage, userObject],
  )

  useEffect(() => {
    profileData && setChooseLang(profileLangLocal || profileData?.preferredLanguage)
  }, [profileData, profileLangLocal])

  useEffect(() => {
    if (vocabList.length === 0 && config.vocabulary) {
      setVocabList(sortByVocabKey(config.vocabulary))
    }
  }, [config.vocabulary, sortByVocabKey, vocabList])

  //focus the right language button after navigation animation finishes
  useEffect(() => {
    if (chooseLang) {
      const timer = setTimeout(() => {
        if (navigationDirectionRef.current === 'next') {
          //after next page navigation, focus first item in first column
          firstColRef.current?.focus()
        } else if (navigationDirectionRef.current === 'prev') {
          //after prev page navigation, focus first item in last column
          lastColRef.current?.focus()
        } else if (!isLoading && selectedLanguageRef.current) {
          // Initial focus on selected language
          selectedLanguageRef.current && selectedLanguageRef.current.focus()
        } else {
          //fallback to cancel button
          cancelButtonRef.current?.focus()
        }
        //reset the navigation direction
        navigationDirectionRef.current = null
      }, 10)

      return () => clearTimeout(timer)
    }
  }, [chooseLang, currentStartColumn, isLoading])

  useEffect(() => {
    showMenu(false)
  }, [showMenu])

  if (isLoading) return <DotSpinner />

  return (
    <div className={classNames('app-lang-info-container', vocabList.length > 7 ? 'left' : 'central')}>
      <ProfileTitle title={V.profileInfoAppLanguageTitle} />
      <div className="app-buttons-container">
        {hasPrevPage && (
          <div className="left-array">
            <RightArrow onClick={handlePrevPage} />
          </div>
        )}

        <div
          className={classNames(
            'column-container',
            isTransitioningRight && 'transition-right',
            isTransitioningLeft && 'transition-left',
          )}
        >
          {profileData?.preferredLanguage &&
            visibleColumns.map((column, colIndex) => (
              <div
                key={`col-${currentStartColumn + colIndex}`}
                className={classNames('language-column', colIndex === 3 && 'preview-column')}
              >
                {column.map((vocabulary: Vocabulary, rowIndex: number) => {
                  const { vocabKey, language } = vocabulary

                  if (colIndex < 3) {
                    //Determines which ref to use
                    let buttonRef = null
                    if (language === chooseLang) {
                      buttonRef = selectedLanguageRef
                    }
                    //First item in first column
                    else if (colIndex === 0 && rowIndex === 0) {
                      buttonRef = firstColRef
                    }
                    //First item in last column
                    else if (colIndex === 2 && rowIndex === 0) {
                      buttonRef = lastColRef
                    }

                    return (
                      <LanguageButton
                        key={vocabKey}
                        language={language}
                        buttonText={V[vocabKey]}
                        buttonRef={buttonRef}
                        setIsLoading={setIsLoading}
                        isLoading={isLoading}
                        setChooseLang={setChooseLang}
                        chooseLang={chooseLang}
                        handleKeyDown={(e: React.KeyboardEvent | React.MouseEvent) =>
                          handleKeyDown(e, colIndex, language)
                        }
                        lastFocusedLanguage={language}
                      />
                    )
                  } else {
                    // preview column of the 4th column-render (non-interactive elements)
                    return (
                      <div key={vocabKey} className={classNames('lang-button ', language === chooseLang && 'selected')}>
                        {V[vocabKey]}
                      </div>
                    )
                  }
                })}
              </div>
            ))}
        </div>

        {hasNextPage && (
          <div className="right-array">
            <RightArrow onClick={handleNextPage} />
          </div>
        )}
      </div>
      <div className="buttons-container">
        <SecondaryButton
          onClick={handleSave}
          disabled={chooseLang === profileData?.preferredLanguage}
          className={classNames(chooseLang === profileData?.preferredLanguage && 'disabled')}
        >
          {V.genericDoneLabel}
        </SecondaryButton>
        <SecondaryButton onClick={handleCancel} buttonRef={cancelButtonRef}>
          {V.genericCancelLabel}
        </SecondaryButton>
      </div>
    </div>
  )
}

export default AppLanguage
